/**
 * The reducer. The only entry point into the rules engine.
 *
 *   reduce(state, action) -> { state, events }
 *
 * `reduce` never mutates its input: it clones first, applies the action to the
 * clone, and returns the clone with the events the action produced.
 *
 * Nothing in this file branches on a card name or id.
 */

import type { ActivatedAbility, Cost, Effect, TriggeredAbility } from './cardTypes';
import { getCardDef, profileOf } from './cardDb';
import {
  autoOrderBlockers,
  canSkipCombat,
  dealCombatDamage,
  declareAttackers,
  declareBlockers,
  endCombat,
  frontrunRelevant,
  legalAttackers,
  legalBlockersFor,
  setBlockerOrder,
  validateBlocks,
} from './combat';
import {
  allTargetsIllegal,
  collectSlots,
  executeEffects,
  legalTargets,
  slotSpecs,
  validateSelection,
} from './effects';
import { canAfford, computeCost, planPayment } from './gas';
import {
  addGas,
  createTokenObject,
  destroyObject,
  discardCard,
  drawCards,
  emit,
  emptyPool,
  enterBattlefield,
  fireTriggers,
  makeCtx,
  moveObject,
  nextId,
  sacrificeObject,
  shuffleDeck,
  syncRng,
  tapWallet,
  type Ctx,
} from './mutate';
import {
  charactersOf,
  handSizeLimit,
  nameOf,
  opponentOf,
  profile,
  walletInstallAllowance,
} from './query';
import { Rng, seedToState } from './rng';
import { isGameOver, runStateBasedChecks, settleGameResult } from './stateBasedChecks';
import {
  DEV_ACTION_TYPES,
  defaultOptions,
  emptyCombat,
  emptyGasPool,
  RulesError,
  type Action,
  type BlockAssignment,
  type GameEvent,
  type GameObject,
  type GameOptions,
  type GameState,
  type PendingChoice,
  type PlayerState,
  type RedactedGameState,
  type StackItem,
  type TargetSelection,
} from './types';

export const STATE_VERSION = 1;

// ---------------------------------------------------------------------------
// Creation
// ---------------------------------------------------------------------------

export interface CreateGameInput {
  seed: number | string;
  players: { id: string; name: string; deck: string[] }[];
  options?: Partial<GameOptions>;
}

export interface CreateGameResult {
  state: GameState;
  events: GameEvent[];
}

function newObject(instanceId: string, cardId: string, owner: string): GameObject {
  return {
    instanceId,
    cardId,
    controller: owner,
    owner,
    zone: 'deck',
    exhausted: false,
    frozen: false,
    damage: 0,
    buffs: [],
    grantedKeywords: [],
    removedKeywords: [],
    enteredTurn: -1,
    controlSinceTurn: -1,
    counters: {},
    oncePerTurnUsed: [],
  };
}

function newPlayer(id: string, name: string, integrity: number): PlayerState {
  return {
    id,
    name,
    integrity,
    deck: [],
    hand: [],
    dust: [],
    blacklist: [],
    battlefield: { characters: [], wallets: [], protocols: [], upgrades: [] },
    gas: emptyGasPool(),
    walletsInstalledThisTurn: 0,
    redraw: 'pending',
    playedThisTurn: [],
    conceded: false,
    lost: false,
    deckExhaustionCount: 0,
  };
}

export function createGame(input: CreateGameInput): CreateGameResult {
  if (input.players.length !== 2) {
    throw new RulesError('DECK_INVALID', 'A match needs exactly two players.');
  }
  const options: GameOptions = { ...defaultOptions(), ...input.options };
  const seed = seedToState(input.seed);

  const state: GameState = {
    version: STATE_VERSION,
    seed,
    rngState: seed,
    options,
    players: {},
    playerOrder: [],
    objects: {},
    nextId: 1,
    turn: 0,
    activePlayer: input.players[0].id,
    priorityPlayer: input.players[0].id,
    phase: 'setup',
    combat: emptyCombat(),
    stack: [],
    passCount: 0,
    pendingAdvance: false,
    pendingChoices: [],
    pendingTriggers: [],
    winner: null,
    draw: false,
    eventLog: [],
  };

  const ctx = makeCtx(state);

  for (const p of input.players) {
    state.players[p.id] = newPlayer(p.id, p.name, options.startingIntegrity);
    state.playerOrder.push(p.id);
    for (const cardId of p.deck) {
      if (!getCardDef(cardId)) {
        throw new RulesError('DECK_INVALID', `Unknown card id in deck: ${cardId}`);
      }
      const instanceId = nextId(ctx, 'o');
      state.objects[instanceId] = newObject(instanceId, cardId, p.id);
      state.players[p.id].deck.push(instanceId);
    }
  }

  // Seeded starting player, then seeded shuffles.
  const startIndex = options.startingPlayer
    ? state.playerOrder.indexOf(options.startingPlayer)
    : ctx.rng.int(state.playerOrder.length);
  const starting = state.playerOrder[Math.max(0, startIndex)];
  // Seating order records who went first.
  state.playerOrder = [starting, ...state.playerOrder.filter((p) => p !== starting)];
  state.activePlayer = starting;
  state.priorityPlayer = starting;

  for (const pid of state.playerOrder) shuffleDeck(ctx, pid);

  emit(ctx, { type: 'GameCreated', turn: 0, seed, startingPlayer: starting });

  for (const pid of state.playerOrder) {
    drawCards(ctx, pid, options.openingHandSize);
    emit(ctx, { type: 'HandDrawn', turn: 0, player: pid, count: options.openingHandSize });
  }

  if (options.skipRedraw) {
    for (const pid of state.playerOrder) state.players[pid].redraw = 'kept';
    beginTurn(ctx);
  }

  syncRng(ctx);
  return { state, events: ctx.events };
}

// ---------------------------------------------------------------------------
// Reduce
// ---------------------------------------------------------------------------

export interface ReduceResult {
  state: GameState;
  events: GameEvent[];
}

/**
 * Clone a state.
 *
 * This is the hottest function in the engine: the simulator runs thousands of
 * games and every action clones a whole state. A hand-written clone is about
 * fifteen times faster than structuredClone here, so it is worth the lines.
 *
 * Two things are shared rather than copied, both deliberately:
 *
 * - `eventLog` entries. The log is append-only history and an event is never
 *   mutated after `emit`, so the array is copied but its entries are shared.
 *   Deep copying them made `reduce` quadratic in the length of the match.
 * - `StackItem.effects`. These point into an immutable card definition and the
 *   interpreter only reads them, which is what "snapshot of the effects" means.
 *
 * Everything else that can be mutated during a `reduce` is copied. A test
 * asserts this is deep-equal to `structuredClone` and that `reduce` never
 * mutates its input.
 */
export function cloneState(state: GameState): GameState {
  const players: GameState['players'] = {};
  for (const pid of state.playerOrder) {
    const p = state.players[pid];
    if (!p) continue;
    players[pid] = {
      ...p,
      deck: p.deck.slice(),
      hand: p.hand.slice(),
      dust: p.dust.slice(),
      blacklist: p.blacklist.slice(),
      battlefield: {
        characters: p.battlefield.characters.slice(),
        wallets: p.battlefield.wallets.slice(),
        protocols: p.battlefield.protocols.slice(),
        upgrades: p.battlefield.upgrades.slice(),
      },
      gas: { ...p.gas },
      playedThisTurn: p.playedThisTurn.slice(),
    };
  }

  const objects: GameState['objects'] = {};
  for (const key in state.objects) {
    const o = state.objects[key];
    objects[key] = {
      ...o,
      buffs: o.buffs.length === 0 ? [] : o.buffs.map((b) => ({ ...b })),
      grantedKeywords: o.grantedKeywords.length === 0 ? [] : o.grantedKeywords.map((g) => ({ ...g })),
      removedKeywords: o.removedKeywords.length === 0 ? [] : o.removedKeywords.map((g) => ({ ...g })),
      counters: { ...o.counters },
      oncePerTurnUsed: o.oncePerTurnUsed.slice(),
    };
  }

  const blockers: Record<string, string[]> = {};
  for (const key in state.combat.blockers) blockers[key] = state.combat.blockers[key].slice();

  return {
    ...state,
    options: { ...state.options },
    players,
    playerOrder: state.playerOrder.slice(),
    objects,
    combat: {
      ...state.combat,
      attackers: state.combat.attackers.slice(),
      blockers,
      dealtFrontrun: state.combat.dealtFrontrun.slice(),
      dealtFrontrunBlockers: state.combat.dealtFrontrunBlockers.slice(),
    },
    stack: state.stack.map(cloneStackItem),
    pendingChoices: state.pendingChoices.map(clonePendingChoice),
    pendingTriggers: state.pendingTriggers.map(cloneStackItem),
    eventLog: state.eventLog.slice(),
  };
}

function cloneStackItem(item: StackItem): StackItem {
  return { ...item, targets: item.targets.map((t) => t.slice()) };
}

function clonePendingChoice(choice: PendingChoice): PendingChoice {
  switch (choice.kind) {
    case 'scry':
      return { ...choice, cards: choice.cards.slice() };
    case 'orderBlockers':
      return { ...choice, blockerIds: choice.blockerIds.slice() };
    case 'sacrifice':
      return { ...choice, legal: choice.legal.slice() };
    case 'chooseTargets':
      return {
        ...choice,
        slots: choice.slots.map((slot) => ({
          ...slot,
          path: slot.path.slice(),
          legal: slot.legal.slice(),
        })),
      };
    default:
      return { ...choice };
  }
}

export function reduce(state: GameState, action: Action): ReduceResult {
  const next = cloneState(state);
  const ctx = makeCtx(next);
  try {
    apply(ctx, action);
  } finally {
    syncRng(ctx);
  }
  return { state: next, events: ctx.events };
}

function apply(ctx: Ctx, action: Action): void {
  const state = ctx.state;

  if (DEV_ACTION_TYPES.includes(action.type)) {
    if (!state.options.devMode) {
      throw new RulesError('INVALID_ACTION', 'Dev actions require Test Lab mode.');
    }
    applyDev(ctx, action);
    runStateBasedChecks(ctx);
    return;
  }

  if (state.phase === 'over') {
    throw new RulesError('INVALID_ACTION', 'The game is over.');
  }

  if (action.type === 'Concede') {
    const p = state.players[action.player];
    if (!p) throw new RulesError('INVALID_ACTION', 'Unknown player.');
    p.conceded = true;
    emit(ctx, { type: 'Conceded', turn: state.turn, player: action.player });
    settleGameResult(ctx);
    return;
  }

  // Pending choices block everything else.
  if (state.pendingChoices.length > 0) {
    applyChoice(ctx, action);
    return;
  }

  if (state.phase === 'setup') {
    applySetup(ctx, action);
    return;
  }

  switch (action.type) {
    case 'InstallWallet':
      installWallet(ctx, action.player, action.cardInstanceId);
      break;
    case 'PlayCard':
      playCard(ctx, action.player, action.cardInstanceId, action.targets ?? [], action.xValue ?? 0);
      break;
    case 'ActivateAbility':
      activateAbility(ctx, action);
      break;
    case 'DeclareAttackers':
      doDeclareAttackers(ctx, action.player, action.attackers);
      break;
    case 'DeclareBlockers':
      doDeclareBlockers(ctx, action.player, action.assignments);
      break;
    case 'OrderBlockers':
      requirePriorityOwner(state, action.player);
      setBlockerOrder(ctx, action.attacker, action.order);
      break;
    case 'Pass':
      requirePriorityOwner(state, action.player);
      emit(ctx, { type: 'Passed', turn: state.turn, player: action.player });
      passPriority(ctx);
      break;
    case 'NextPhase':
      nextPhase(ctx, action.player);
      break;
    default:
      throw new RulesError('INVALID_ACTION', `Action ${action.type} is not available now.`);
  }

  runStateBasedChecks(ctx);
}

// ---------------------------------------------------------------------------
// Setup and redraw
// ---------------------------------------------------------------------------

function applySetup(ctx: Ctx, action: Action): void {
  const state = ctx.state;
  if (action.type !== 'Redraw' && action.type !== 'KeepHand') {
    throw new RulesError('WRONG_PHASE', 'Decide your opening hand first.');
  }
  const p = state.players[action.player];
  if (!p) throw new RulesError('INVALID_ACTION', 'Unknown player.');
  if (p.redraw !== 'pending') {
    throw new RulesError('INVALID_ACTION', 'You already decided on your opening hand.');
  }
  if (action.type === 'KeepHand') {
    p.redraw = 'kept';
    emit(ctx, { type: 'HandKept', turn: 0, player: action.player });
  } else {
    for (const id of p.hand.slice()) moveObject(ctx, id, 'deck', { deckPosition: 'bottom' });
    shuffleDeck(ctx, action.player);
    drawCards(ctx, action.player, state.options.redrawHandSize);
    p.redraw = 'redrawn';
    emit(ctx, {
      type: 'HandRedrawn',
      turn: 0,
      player: action.player,
      count: state.options.redrawHandSize,
    });
  }
  if (state.playerOrder.every((pid) => state.players[pid].redraw !== 'pending')) {
    beginTurn(ctx);
  }
}

// ---------------------------------------------------------------------------
// Turn flow
// ---------------------------------------------------------------------------

function beginTurn(ctx: Ctx): void {
  const state = ctx.state;
  const first = state.turn === 0;
  state.turn += 1;
  if (!first) {
    state.activePlayer = opponentOf(state, state.activePlayer);
  }
  const active = state.activePlayer;
  const p = state.players[active];
  p.walletsInstalledThisTurn = 0;
  p.playedThisTurn = [];
  state.combat = emptyCombat();
  emit(ctx, { type: 'TurnStarted', turn: state.turn, player: active });

  // SYNC
  state.phase = 'sync';
  emit(ctx, { type: 'PhaseChanged', turn: state.turn, player: active, phase: 'sync' });
  const synced: string[] = [];
  const rows = p.battlefield;
  for (const id of [...rows.wallets, ...rows.characters, ...rows.protocols, ...rows.upgrades]) {
    const o = state.objects[id];
    if (!o) continue;
    if (o.frozen) {
      o.frozen = false;
      emit(ctx, {
        type: 'Unfroze',
        turn: state.turn,
        player: active,
        instanceId: id,
        name: nameOf(state, id),
      });
      continue;
    }
    if (o.exhausted) {
      o.exhausted = false;
      synced.push(id);
    }
  }
  if (synced.length > 0) {
    emit(ctx, { type: 'CardsSynced', turn: state.turn, player: active, instanceIds: synced });
  }
  fireTriggers(ctx, 'turn:start', { controller: active });

  // DRAW
  enterPhase(ctx, 'draw');
  const skipDraw = state.turn === 1 && active === state.playerOrder[0];
  if (!skipDraw) drawCards(ctx, active, 1);

  runStateBasedChecks(ctx);
  if (isGameOver(state)) return;

  enterPhase(ctx, 'main1');
  grantPriority(ctx, active);
}

function enterPhase(ctx: Ctx, phase: GameState['phase']): void {
  const state = ctx.state;
  for (const pid of state.playerOrder) emptyPool(ctx, pid);
  state.phase = phase;
  emit(ctx, {
    type: 'PhaseChanged',
    turn: state.turn,
    player: state.activePlayer,
    phase,
  });
}

function nextPhase(ctx: Ctx, playerId: string): void {
  const state = ctx.state;
  if (playerId !== state.activePlayer) {
    throw new RulesError('NOT_YOUR_TURN', 'Only the active player advances the phase.');
  }
  if (state.priorityPlayer !== playerId) {
    throw new RulesError('NO_PRIORITY', 'You do not have priority.');
  }
  if (state.stack.length > 0) {
    throw new RulesError('STACK_NOT_EMPTY', 'Resolve the stack first.');
  }
  if (state.phase === 'combat' && state.combat.step === 'declareBlockers') {
    throw new RulesError('WRONG_PHASE', 'The defender must declare blockers.');
  }
  openWindow(ctx);
}

/**
 * Open a priority window before the game moves on.
 *
 * Queued triggers go on the stack first. If anything is on the stack, or a
 * player actually holds a playable response, that player gets priority and the
 * advance waits. Otherwise the game moves on straight away, which is what
 * keeps play snappy (RULES 3).
 *
 * `includeActive` is true for windows opened by a combat step, where the
 * active player has just learned something new and may want to act. It is
 * false for a window opened by NEXT PHASE, where the active player has already
 * said they are done.
 */
function openWindow(ctx: Ctx, includeActive = false): void {
  const state = ctx.state;
  promoteTriggers(ctx);
  if (state.stack.length > 0) {
    state.pendingAdvance = true;
    grantPriority(ctx, state.activePlayer);
    return;
  }
  const opp = opponentOf(state, state.activePlayer);
  if (includeActive && hasPlayableResponse(state, state.activePlayer)) {
    state.pendingAdvance = true;
    grantPriority(ctx, state.activePlayer);
    return;
  }
  if (hasPlayableResponse(state, opp)) {
    state.pendingAdvance = true;
    grantPriority(ctx, opp);
    return;
  }
  advance(ctx);
}

function advance(ctx: Ctx): void {
  const state = ctx.state;
  state.pendingAdvance = false;
  switch (state.phase) {
    case 'main1':
      enterCombat(ctx);
      break;
    case 'combat':
      advanceCombatStep(ctx);
      break;
    case 'main2':
      enterEndStep(ctx);
      break;
    case 'end':
      finishTurn(ctx);
      break;
    default:
      break;
  }
}

function enterCombat(ctx: Ctx): void {
  const state = ctx.state;
  if (canSkipCombat(state, state.activePlayer)) {
    enterPhase(ctx, 'main2');
    grantPriority(ctx, state.activePlayer);
    return;
  }
  enterPhase(ctx, 'combat');
  enterCombatStep(ctx, 'declareAttackers');
}

function enterCombatStep(ctx: Ctx, step: GameState['combat']['step']): void {
  const state = ctx.state;
  for (const pid of state.playerOrder) emptyPool(ctx, pid);
  state.combat.step = step;
  emit(ctx, {
    type: 'CombatStepChanged',
    turn: state.turn,
    player: state.activePlayer,
    step,
  });
  const active = state.activePlayer;
  const defender = opponentOf(state, active);

  switch (step) {
    case 'declareAttackers':
      grantPriority(ctx, active);
      break;
    case 'attackersDeclared':
      openWindow(ctx, true);
      break;
    case 'declareBlockers':
      grantPriority(ctx, defender);
      break;
    case 'blockersDeclared':
      openWindow(ctx, true);
      break;
    case 'frontrunDamage':
      if (frontrunRelevant(state)) {
        dealCombatDamage(ctx, 'frontrun');
        runStateBasedChecks(ctx);
        if (isGameOver(state)) return;
        openWindow(ctx, true);
      } else {
        enterCombatStep(ctx, 'regularDamage');
      }
      break;
    case 'regularDamage':
      dealCombatDamage(ctx, 'regular');
      runStateBasedChecks(ctx);
      if (isGameOver(state)) return;
      enterCombatStep(ctx, 'combatEnd');
      break;
    case 'combatEnd':
      endCombat(ctx);
      enterPhase(ctx, 'main2');
      grantPriority(ctx, active);
      break;
    default:
      break;
  }
}

function advanceCombatStep(ctx: Ctx): void {
  const state = ctx.state;
  switch (state.combat.step) {
    case 'declareAttackers':
      // No attackers declared: combat ends.
      endCombat(ctx);
      enterPhase(ctx, 'main2');
      grantPriority(ctx, state.activePlayer);
      break;
    case 'attackersDeclared':
      enterCombatStep(ctx, 'declareBlockers');
      break;
    case 'declareBlockers':
      enterCombatStep(ctx, 'blockersDeclared');
      break;
    case 'blockersDeclared':
      enterCombatStep(ctx, 'frontrunDamage');
      break;
    case 'frontrunDamage':
      enterCombatStep(ctx, 'regularDamage');
      break;
    case 'regularDamage':
      enterCombatStep(ctx, 'combatEnd');
      break;
    default:
      enterPhase(ctx, 'main2');
      grantPriority(ctx, state.activePlayer);
      break;
  }
}

function enterEndStep(ctx: Ctx): void {
  const state = ctx.state;
  enterPhase(ctx, 'end');
  fireTriggers(ctx, 'turn:end', { controller: state.activePlayer });
  openWindow(ctx);
}

function finishTurn(ctx: Ctx): void {
  const state = ctx.state;
  const active = state.activePlayer;
  const limit = handSizeLimit(state, active);
  const over = state.players[active].hand.length - limit;
  if (over > 0) {
    emit(ctx, { type: 'HandSizeDiscard', turn: state.turn, player: active, count: over });
    state.pendingChoices.push({ kind: 'discard', player: active, count: over });
    return;
  }
  cleanupAndPassTurn(ctx);
}

/** Damage removal and "until end of turn" expiry (RULES 3). */
function cleanupAndPassTurn(ctx: Ctx): void {
  const state = ctx.state;
  let expired = 0;
  for (const id of Object.keys(state.objects)) {
    const o = state.objects[id];
    o.damage = 0;
    const before = o.buffs.length + o.grantedKeywords.length + o.removedKeywords.length;
    o.buffs = o.buffs.filter((b) => b.duration !== 'turn');
    o.grantedKeywords = o.grantedKeywords.filter((g) => g.duration !== 'turn');
    o.removedKeywords = o.removedKeywords.filter((g) => g.duration !== 'turn');
    expired += before - (o.buffs.length + o.grantedKeywords.length + o.removedKeywords.length);
    o.oncePerTurnUsed = [];
    delete o.counters.drainerLinkMarked;
  }
  emit(ctx, {
    type: 'EffectsExpired',
    turn: state.turn,
    player: state.activePlayer,
    count: expired,
  });
  for (const pid of state.playerOrder) emptyPool(ctx, pid);
  runStateBasedChecks(ctx);
  if (isGameOver(state)) return;
  beginTurn(ctx);
}

// ---------------------------------------------------------------------------
// Priority and the stack
// ---------------------------------------------------------------------------

function grantPriority(ctx: Ctx, playerId: string): void {
  const state = ctx.state;
  state.priorityPlayer = playerId;
  state.passCount = 0;
  promoteTriggers(ctx);
  emit(ctx, { type: 'PriorityChanged', turn: state.turn, player: playerId });
}

function requirePriorityOwner(state: GameState, playerId: string): void {
  if (state.priorityPlayer !== playerId) {
    throw new RulesError('NO_PRIORITY', 'You do not have priority.');
  }
}

/** Queued triggers go on the stack in APNAP order, asking for targets. */
function promoteTriggers(ctx: Ctx): void {
  const state = ctx.state;
  if (state.pendingTriggers.length === 0) return;
  const queue = state.pendingTriggers;
  state.pendingTriggers = [];
  for (const item of queue) {
    const specs = slotSpecs(state, item.effects, item.controller, item.sourceInstanceId, item.id);
    const chosen: TargetSelection[] = [];
    const needsInput: typeof specs = [];
    for (const spec of specs) {
      if (spec.legal.length === 0) chosen.push([]);
      else if (spec.legal.length <= spec.count) chosen.push(spec.legal.slice());
      else {
        chosen.push([]);
        needsInput.push(spec);
      }
    }
    item.targets = chosen;
    state.stack.push(item);
    if (needsInput.length > 0) {
      state.pendingChoices.push({
        kind: 'chooseTargets',
        player: item.controller,
        stackItemId: item.id,
        slots: specs,
      });
    }
  }
}

function passPriority(ctx: Ctx): void {
  const state = ctx.state;
  if (state.stack.length > 0) {
    state.passCount += 1;
    if (state.passCount >= state.playerOrder.length) {
      state.passCount = 0;
      resolveTop(ctx);
      if (isGameOver(state)) return;
      if (state.stack.length === 0 && state.pendingAdvance) {
        // The window that opened the advance is still open, so try again.
        openWindow(ctx, true);
        return;
      }
      grantPriority(ctx, state.activePlayer);
      return;
    }
    grantPriorityKeepingCount(ctx, opponentOf(state, state.priorityPlayer));
    return;
  }
  if (state.pendingAdvance) {
    // APNAP: the active player passes first, then the opponent, then it moves.
    if (state.priorityPlayer === state.activePlayer) {
      const opp = opponentOf(state, state.activePlayer);
      if (hasPlayableResponse(state, opp)) {
        grantPriority(ctx, opp);
        state.pendingAdvance = true;
        return;
      }
    }
    advance(ctx);
    return;
  }
  if (state.priorityPlayer !== state.activePlayer) {
    grantPriority(ctx, state.activePlayer);
    return;
  }
  // The active player passing on an empty stack is the same as advancing.
  openWindow(ctx);
}

function grantPriorityKeepingCount(ctx: Ctx, playerId: string): void {
  const state = ctx.state;
  const keep = state.passCount;
  state.priorityPlayer = playerId;
  state.passCount = keep;
  promoteTriggers(ctx);
  emit(ctx, { type: 'PriorityChanged', turn: state.turn, player: playerId });
}

function resolveTop(ctx: Ctx): void {
  const state = ctx.state;
  const item = state.stack.pop();
  if (!item) return;

  if (allTargetsIllegal(state, item)) {
    emit(ctx, { type: 'Fizzled', turn: state.turn, player: item.controller, label: item.label });
    if (item.kind === 'card') moveObject(ctx, item.sourceInstanceId, 'dust');
    runStateBasedChecks(ctx);
    return;
  }

  if (item.kind === 'card') {
    const prof = profile(state, item.sourceInstanceId);
    const o = state.objects[item.sourceInstanceId];
    if (o) o.xValue = item.xValue;
    if (prof.type === 'upgrade') {
      const host = item.hostInstanceId ? state.objects[item.hostInstanceId] : undefined;
      if (!host || host.zone !== 'battlefield') {
        emit(ctx, { type: 'Fizzled', turn: state.turn, player: item.controller, label: item.label });
        moveObject(ctx, item.sourceInstanceId, 'dust');
      } else {
        enterBattlefield(ctx, item.sourceInstanceId, { attachedTo: item.hostInstanceId });
      }
    } else if (prof.type === 'character' || prof.type === 'protocol' || prof.type === 'wallet') {
      enterBattlefield(ctx, item.sourceInstanceId, {
        exhausted: prof.wallet?.entersExhausted === true,
      });
    } else {
      executeEffects(ctx, item);
      moveObject(ctx, item.sourceInstanceId, 'dust');
    }
  } else {
    executeEffects(ctx, item);
  }

  emit(ctx, {
    type: 'StackItemResolved',
    turn: state.turn,
    player: item.controller,
    stackItemId: item.id,
    label: item.label,
  });
  runStateBasedChecks(ctx);
}

// ---------------------------------------------------------------------------
// Playing cards
// ---------------------------------------------------------------------------

/** Effects a Transaction or Exploit performs: its `self:deploy` abilities. */
export function spellEffects(abilities: readonly { kind: string }[]): Effect[] {
  const out: Effect[] = [];
  for (const a of abilities) {
    if (a.kind !== 'triggered') continue;
    const t = a as unknown as TriggeredAbility;
    if (t.event !== 'self:deploy') continue;
    out.push(...t.effects);
  }
  return out;
}

function installWallet(ctx: Ctx, playerId: string, instanceId: string): void {
  const state = ctx.state;
  requirePriorityOwner(state, playerId);
  if (playerId !== state.activePlayer) {
    throw new RulesError('NOT_YOUR_TURN', 'You can only install a Wallet on your turn.');
  }
  if (state.phase !== 'main1' && state.phase !== 'main2') {
    throw new RulesError('WRONG_PHASE', 'Wallets install during your Main phases.');
  }
  if (state.stack.length > 0) {
    throw new RulesError('STACK_NOT_EMPTY', 'Resolve the stack first.');
  }
  const o = state.objects[instanceId];
  if (!o || o.zone !== 'hand' || o.owner !== playerId) {
    throw new RulesError('INVALID_ACTION', 'That card is not in your hand.');
  }
  const prof = profileOf(o);
  if (prof.type !== 'wallet') {
    throw new RulesError('INVALID_ACTION', 'That card is not a Wallet.');
  }
  const p = state.players[playerId];
  if (p.walletsInstalledThisTurn >= walletInstallAllowance(state, playerId)) {
    throw new RulesError('WALLET_ALREADY_INSTALLED', 'Already installed a Wallet this turn.');
  }
  p.walletsInstalledThisTurn += 1;
  p.playedThisTurn.push('wallet');
  enterBattlefield(ctx, instanceId, { exhausted: prof.wallet?.entersExhausted === true });
  emit(ctx, {
    type: 'WalletInstalled',
    turn: state.turn,
    player: playerId,
    instanceId,
    name: prof.name,
  });
  promoteTriggers(ctx);
}

/**
 * Pay a cost with the auto-pay algorithm: plan it, exhaust the planned Wallets
 * (which pushes their Gas into the pool), then take the cost out of the pool.
 * Whatever the taps produced beyond the cost stays in the pool for this phase.
 */
function payCost(ctx: Ctx, playerId: string, cost: Cost, excludeInstanceId: string, cardName: string): void {
  const state = ctx.state;
  const plan = planPayment(state, playerId, cost, excludeInstanceId);
  if (!plan.ok) {
    throw new RulesError('NOT_ENOUGH_GAS', plan.reason ?? 'Not enough Gas.');
  }
  for (const id of plan.walletsToTap) tapWallet(ctx, id);

  const pool = state.players[playerId].gas;
  const chains: ('btc' | 'eth' | 'sol' | 'bnb' | 'xrp')[] = ['btc', 'eth', 'sol', 'bnb', 'xrp'];
  for (const c of chains) {
    let need = cost[c] ?? 0;
    const take = Math.min(pool[c], need);
    pool[c] -= take;
    need -= take;
    if (need > 0) {
      throw new RulesError('NOT_ENOUGH_GAS', `Needs ${need} more ${c.toUpperCase()} Gas.`);
    }
  }
  let generic = cost.generic ?? 0;
  const order: ('generic' | 'btc' | 'eth' | 'sol' | 'bnb' | 'xrp')[] = [
    'generic',
    'btc',
    'eth',
    'sol',
    'bnb',
    'xrp',
  ];
  for (const key of order) {
    if (generic <= 0) break;
    const take = Math.min(pool[key], generic);
    pool[key] -= take;
    generic -= take;
  }
  if (generic > 0) {
    throw new RulesError('NOT_ENOUGH_GAS', `Needs ${generic} more generic Gas.`);
  }
  emit(ctx, { type: 'GasSpent', turn: state.turn, player: playerId, cost, cardName });
}

function playCard(
  ctx: Ctx,
  playerId: string,
  instanceId: string,
  targets: TargetSelection[],
  xValue: number,
): void {
  const state = ctx.state;
  requirePriorityOwner(state, playerId);
  const o = state.objects[instanceId];
  if (!o || o.zone !== 'hand' || o.owner !== playerId) {
    throw new RulesError('INVALID_ACTION', 'That card is not in your hand.');
  }
  const prof = profileOf(o);
  if (prof.type === 'wallet') {
    throw new RulesError('INVALID_ACTION', 'Install Wallets with INSTALL, not PLAY.');
  }

  const instantSpeed = prof.type === 'exploit';
  if (!instantSpeed) {
    if (playerId !== state.activePlayer) {
      throw new RulesError('NOT_YOUR_TURN', 'Only Exploits can be played on the opponent turn.');
    }
    if (state.phase !== 'main1' && state.phase !== 'main2') {
      throw new RulesError('WRONG_PHASE', 'That card can only be played in a Main phase.');
    }
    if (state.stack.length > 0) {
      throw new RulesError('STACK_NOT_EMPTY', 'Only Exploits can be played while the stack is busy.');
    }
  }

  // An Upgrade needs a friendly character host.
  let hostInstanceId: string | undefined;
  let slotTargets = targets;
  if (prof.type === 'upgrade') {
    const hosts = charactersOf(state, playerId).map((c) => c.instanceId);
    if (hosts.length === 0) {
      throw new RulesError('NO_LEGAL_TARGET', 'That Upgrade needs a friendly character.');
    }
    const requested = targets[0]?.[0];
    hostInstanceId = requested ?? hosts[0];
    if (!hosts.includes(hostInstanceId)) {
      throw new RulesError('ILLEGAL_TARGET', 'That is not a friendly character.');
    }
    slotTargets = targets.slice(1);
  }

  const effects = prof.type === 'transaction' || prof.type === 'exploit' ? spellEffects(prof.abilities) : [];
  const specs = slotSpecs(state, effects, playerId, instanceId);
  const valid = validateSelection(specs, slotTargets);
  if (!valid.ok) throw new RulesError(valid.code, valid.reason);

  const cost = computeCost(state, instanceId, prof.cost.x ? Math.max(0, xValue) : 0);
  payCost(ctx, playerId, cost, instanceId, prof.name);

  o.xValue = prof.cost.x ? Math.max(0, xValue) : undefined;
  state.players[playerId].playedThisTurn.push(prof.type);
  moveObject(ctx, instanceId, 'stack');

  const item: StackItem = {
    id: nextId(ctx, 'st'),
    kind: 'card',
    controller: playerId,
    sourceInstanceId: instanceId,
    effects,
    targets: slotTargets,
    xValue: o.xValue,
    hostInstanceId,
    label: prof.name,
  };
  state.stack.push(item);
  emit(ctx, {
    type: 'CardPlayed',
    turn: state.turn,
    player: playerId,
    instanceId,
    name: prof.name,
    cardType: prof.type,
  });
  if (prof.type === 'transaction' || prof.type === 'exploit') {
    fireTriggers(ctx, 'spell:play', { spellInstanceId: instanceId, spellController: playerId });
  }
  grantPriority(ctx, playerId);
}

// ---------------------------------------------------------------------------
// Activated abilities
// ---------------------------------------------------------------------------

function activateAbility(
  ctx: Ctx,
  action: Extract<Action, { type: 'ActivateAbility' }>,
): void {
  const state = ctx.state;
  const playerId = action.player;
  requirePriorityOwner(state, playerId);
  const o = state.objects[action.instanceId];
  if (!o || o.controller !== playerId) {
    throw new RulesError('INVALID_ACTION', 'You do not control that card.');
  }
  const prof = profileOf(o);
  const ability = prof.abilities[action.abilityIndex];
  if (!ability || ability.kind !== 'activated') {
    throw new RulesError('INVALID_ACTION', 'That ability does not exist.');
  }
  const act = ability as ActivatedAbility;

  if (act.cost.removeFromDust) {
    if (o.zone !== 'dust') {
      throw new RulesError('INVALID_ACTION', 'That ability is used from the Dust.');
    }
  } else if (o.zone !== 'battlefield') {
    throw new RulesError('INVALID_ACTION', 'That card is not on the battlefield.');
  }

  if (act.speed === 'main') {
    if (playerId !== state.activePlayer) {
      throw new RulesError('NOT_YOUR_TURN', 'That ability is Main phase only.');
    }
    if (state.phase !== 'main1' && state.phase !== 'main2') {
      throw new RulesError('WRONG_PHASE', 'That ability is Main phase only.');
    }
    if (state.stack.length > 0) {
      throw new RulesError('STACK_NOT_EMPTY', 'Resolve the stack first.');
    }
  }

  const key = `ability:${action.abilityIndex}`;
  if (act.oncePerTurn && o.oncePerTurnUsed.includes(key)) {
    throw new RulesError('ONCE_PER_TURN_USED', 'That ability has already been used this turn.');
  }
  if (act.cost.exhaust && o.exhausted) {
    throw new RulesError('NOT_READY', `${prof.name} is exhausted.`);
  }
  if (act.cost.payIntegrity && state.players[playerId].integrity <= act.cost.payIntegrity) {
    throw new RulesError('INVALID_ACTION', 'Not enough Integrity to pay that cost.');
  }

  const costChoices = action.costChoices ?? [];
  // Sacrifice another friendly permanent matching the filter.
  let sacrificeIds: string[] = [];
  if (act.cost.sacrifice) {
    const pool = legalTargets(
      state,
      { type: 'permanent', who: 'friendly', mode: 'choose', filter: act.cost.sacrifice },
      playerId,
      action.instanceId,
    ).filter((id) => id !== action.instanceId);
    if (pool.length === 0) {
      throw new RulesError('NO_LEGAL_TARGET', 'Nothing to sacrifice for that ability.');
    }
    const chosen = costChoices.filter((id) => pool.includes(id));
    sacrificeIds = chosen.length > 0 ? [chosen[0]] : [pool[0]];
  }
  let discardIds: string[] = [];
  if (act.cost.discard && act.cost.discard > 0) {
    const hand = state.players[playerId].hand.filter((id) => id !== action.instanceId);
    if (hand.length < act.cost.discard) {
      throw new RulesError('INVALID_ACTION', 'Not enough cards in hand to discard.');
    }
    const chosen = costChoices.filter((id) => hand.includes(id));
    discardIds = chosen.length >= act.cost.discard ? chosen.slice(0, act.cost.discard) : hand.slice(0, act.cost.discard);
  }

  const specs = slotSpecs(state, act.effects, playerId, action.instanceId);
  const valid = validateSelection(specs, action.targets ?? []);
  if (!valid.ok) throw new RulesError(valid.code, valid.reason);

  // Pay.
  if (act.cost.gas) payCost(ctx, playerId, act.cost.gas, action.instanceId, prof.name);
  if (act.cost.exhaust) {
    o.exhausted = true;
    emit(ctx, {
      type: 'Exhausted',
      turn: state.turn,
      player: playerId,
      instanceId: action.instanceId,
      name: prof.name,
    });
  }
  for (const id of discardIds) discardCard(ctx, playerId, id);
  for (const id of sacrificeIds) sacrificeObject(ctx, id);
  if (act.cost.payIntegrity) {
    const p = state.players[playerId];
    p.integrity -= act.cost.payIntegrity;
    emit(ctx, {
      type: 'IntegrityChanged',
      turn: state.turn,
      player: playerId,
      delta: -act.cost.payIntegrity,
      total: p.integrity,
      reason: 'ability cost',
    });
  }
  if (act.cost.sacrificeSelf) sacrificeObject(ctx, action.instanceId);
  if (act.cost.removeFromDust) moveObject(ctx, action.instanceId, 'blacklist');
  if (act.oncePerTurn) o.oncePerTurnUsed.push(key);

  const item: StackItem = {
    id: nextId(ctx, 'st'),
    kind: 'ability',
    controller: playerId,
    sourceInstanceId: action.instanceId,
    abilityIndex: action.abilityIndex,
    effects: act.effects,
    targets: action.targets ?? [],
    label: `${prof.name}${act.label ? `: ${act.label}` : ''}`,
  };
  state.stack.push(item);
  emit(ctx, {
    type: 'AbilityActivated',
    turn: state.turn,
    player: playerId,
    instanceId: action.instanceId,
    name: prof.name,
    label: act.label ?? '',
  });
  grantPriority(ctx, playerId);
}

// ---------------------------------------------------------------------------
// Combat actions
// ---------------------------------------------------------------------------

function doDeclareAttackers(ctx: Ctx, playerId: string, attackers: string[]): void {
  const state = ctx.state;
  if (playerId !== state.activePlayer) {
    throw new RulesError('NOT_YOUR_TURN', 'Only the active player declares attackers.');
  }
  if (state.phase !== 'combat' || state.combat.step !== 'declareAttackers') {
    throw new RulesError('WRONG_PHASE', 'It is not the declare attackers step.');
  }
  if (attackers.length === 0) {
    endCombat(ctx);
    enterPhase(ctx, 'main2');
    grantPriority(ctx, playerId);
    return;
  }
  declareAttackers(ctx, playerId, attackers);
  enterCombatStep(ctx, 'attackersDeclared');
}

function doDeclareBlockers(ctx: Ctx, playerId: string, assignments: BlockAssignment[]): void {
  const state = ctx.state;
  const defender = opponentOf(state, state.activePlayer);
  if (playerId !== defender) {
    throw new RulesError('CANT_BLOCK', 'Only the defending player declares blockers.');
  }
  if (state.phase !== 'combat' || state.combat.step !== 'declareBlockers') {
    throw new RulesError('WRONG_PHASE', 'It is not the declare blockers step.');
  }
  declareBlockers(ctx, playerId, assignments);
  // The attacker's controller orders multiple blockers before damage (RULES 6.4).
  let asked = false;
  for (const attackerId of state.combat.attackers) {
    const list = state.combat.blockers[attackerId];
    if (!list || list.length < 2) continue;
    state.pendingChoices.push({
      kind: 'orderBlockers',
      player: state.activePlayer,
      attackerId,
      blockerIds: list.slice(),
    });
    asked = true;
  }
  if (asked) return;
  enterCombatStep(ctx, 'blockersDeclared');
}

// ---------------------------------------------------------------------------
// Choices
// ---------------------------------------------------------------------------

function applyChoice(ctx: Ctx, action: Action): void {
  const state = ctx.state;
  const choice = state.pendingChoices[0];

  if (action.type === 'ChooseTargets' && choice.kind === 'chooseTargets') {
    if (action.player !== choice.player) {
      throw new RulesError('NO_PRIORITY', 'That choice is not yours to make.');
    }
    if (action.stackItemId !== choice.stackItemId) {
      throw new RulesError('INVALID_ACTION', 'That is not the pending stack item.');
    }
    const valid = validateSelection(choice.slots, action.targets);
    if (!valid.ok) throw new RulesError(valid.code, valid.reason);
    const item = state.stack.find((s) => s.id === choice.stackItemId);
    if (item) item.targets = action.targets;
    state.pendingChoices.shift();
    afterChoice(ctx);
    return;
  }

  if (action.type === 'Discard' && choice.kind === 'discard') {
    if (action.player !== choice.player) {
      throw new RulesError('NO_PRIORITY', 'That choice is not yours to make.');
    }
    const hand = state.players[choice.player].hand;
    const ids = [...new Set(action.cardInstanceIds)];
    if (ids.length !== choice.count) {
      throw new RulesError('INVALID_ACTION', `Discard exactly ${choice.count} card(s).`);
    }
    for (const id of ids) {
      if (!hand.includes(id)) throw new RulesError('INVALID_ACTION', 'That card is not in your hand.');
    }
    for (const id of ids) discardCard(ctx, choice.player, id);
    state.pendingChoices.shift();
    afterChoice(ctx);
    return;
  }

  if (action.type === 'Scry' && choice.kind === 'scry') {
    if (action.player !== choice.player) {
      throw new RulesError('NO_PRIORITY', 'That choice is not yours to make.');
    }
    const toBottom = [...new Set(action.toBottom)];
    for (const id of toBottom) {
      if (!choice.cards.includes(id)) {
        throw new RulesError('INVALID_ACTION', 'That card is not among the cards you looked at.');
      }
    }
    // Everything kept on top stays in its current relative order.
    for (const id of choice.cards) {
      if (toBottom.includes(id)) moveObject(ctx, id, 'deck', { deckPosition: 'bottom' });
    }
    emit(ctx, {
      type: 'Scried',
      turn: state.turn,
      player: choice.player,
      look: choice.cards.length,
      toBottom: toBottom.length,
    });
    state.pendingChoices.shift();
    afterChoice(ctx);
    return;
  }

  if (action.type === 'Sacrifice' && choice.kind === 'sacrifice') {
    if (action.player !== choice.player) {
      throw new RulesError('NO_PRIORITY', 'That choice is not yours to make.');
    }
    const ids = [...new Set(action.cardInstanceIds)].filter((id) => choice.legal.includes(id));
    if (ids.length !== Math.min(choice.count, choice.legal.length)) {
      throw new RulesError('INVALID_ACTION', 'Choose the right number of permanents to sacrifice.');
    }
    for (const id of ids) sacrificeObject(ctx, id);
    state.pendingChoices.shift();
    afterChoice(ctx);
    return;
  }

  if (action.type === 'OrderBlockers' && choice.kind === 'orderBlockers') {
    if (action.player !== choice.player) {
      throw new RulesError('NO_PRIORITY', 'That choice is not yours to make.');
    }
    if (action.attacker !== choice.attackerId) {
      throw new RulesError('INVALID_ACTION', 'That is not the attacker waiting for an order.');
    }
    setBlockerOrder(ctx, action.attacker, action.order);
    state.pendingChoices.shift();
    afterChoice(ctx);
    return;
  }

  throw new RulesError('INVALID_ACTION', 'Answer the pending choice first.');
}

function afterChoice(ctx: Ctx): void {
  const state = ctx.state;
  runStateBasedChecks(ctx);
  if (isGameOver(state)) return;
  if (state.pendingChoices.length > 0) return;
  if (state.phase === 'end') {
    finishTurn(ctx);
    return;
  }
  if (state.phase === 'combat' && state.combat.step === 'declareBlockers') {
    enterCombatStep(ctx, 'blockersDeclared');
  }
}

// ---------------------------------------------------------------------------
// Dev actions
// ---------------------------------------------------------------------------

function applyDev(ctx: Ctx, action: Action): void {
  const state = ctx.state;
  switch (action.type) {
    case 'DevGiveCard': {
      const instanceId = nextId(ctx, 'o');
      state.objects[instanceId] = newObject(instanceId, action.cardId, action.player);
      state.players[action.player].hand.push(instanceId);
      state.objects[instanceId].zone = 'hand';
      devEvent(ctx, action.player, `gave ${action.cardId}`);
      break;
    }
    case 'DevPutIntoPlay': {
      if (action.tokenId) {
        createTokenObject(ctx, action.player, action.tokenId);
      } else if (action.cardId) {
        const instanceId = nextId(ctx, 'o');
        state.objects[instanceId] = newObject(instanceId, action.cardId, action.player);
        const prof = profile(state, instanceId);
        if (prof.type === 'upgrade') {
          const host = charactersOf(state, action.player)[0];
          if (host) enterBattlefield(ctx, instanceId, { attachedTo: host.instanceId });
        } else {
          enterBattlefield(ctx, instanceId, {});
        }
      }
      devEvent(ctx, action.player, `put into play ${action.cardId ?? action.tokenId ?? ''}`);
      break;
    }
    case 'DevSetGas': {
      state.players[action.player].gas = { ...state.players[action.player].gas, ...action.gas };
      devEvent(ctx, action.player, 'set gas');
      break;
    }
    case 'DevSetIntegrity': {
      state.players[action.player].integrity = action.integrity;
      devEvent(ctx, action.player, `set integrity ${action.integrity}`);
      break;
    }
    case 'DevSetTurn': {
      state.turn = action.turn;
      devEvent(ctx, state.activePlayer, `set turn ${action.turn}`);
      break;
    }
    case 'DevDraw': {
      drawCards(ctx, action.player, action.amount);
      devEvent(ctx, action.player, `drew ${action.amount}`);
      break;
    }
    case 'DevShuffle': {
      shuffleDeck(ctx, action.player);
      devEvent(ctx, action.player, 'shuffled');
      break;
    }
    case 'DevCreateToken': {
      for (let i = 0; i < (action.amount ?? 1); i++) {
        createTokenObject(ctx, action.player, action.tokenId);
      }
      devEvent(ctx, action.player, `created ${action.tokenId}`);
      break;
    }
    case 'DevDestroy': {
      const o = state.objects[action.instanceId];
      destroyObject(ctx, action.instanceId, 'dev');
      devEvent(ctx, o?.controller ?? state.activePlayer, 'destroyed an object');
      break;
    }
    case 'DevClearBoard': {
      for (const pid of state.playerOrder) {
        const bf = state.players[pid].battlefield;
        for (const id of [...bf.characters, ...bf.protocols, ...bf.upgrades, ...bf.wallets]) {
          moveObject(ctx, id, 'dust');
        }
      }
      devEvent(ctx, state.activePlayer, 'cleared the board');
      break;
    }
    case 'DevSkipPhase': {
      state.stack = [];
      state.pendingChoices = [];
      state.pendingAdvance = false;
      advance(ctx);
      devEvent(ctx, state.activePlayer, 'skipped a phase');
      break;
    }
    case 'DevSetDeckOrder': {
      const p = state.players[action.player];
      const known = action.instanceIds.filter((id) => p.deck.includes(id));
      p.deck = [...known, ...p.deck.filter((id) => !known.includes(id))];
      devEvent(ctx, action.player, 'set deck order');
      break;
    }
    default:
      throw new RulesError('INVALID_ACTION', 'Unknown dev action.');
  }
}

function devEvent(ctx: Ctx, playerId: string, detail: string): void {
  emit(ctx, { type: 'DevAction', turn: ctx.state.turn, player: playerId, detail });
}

// ---------------------------------------------------------------------------
// Legal actions
// ---------------------------------------------------------------------------

/**
 * Every action this player can take right now.
 *
 * Combat declarations and multi-target cards are enumerated representatively,
 * not exhaustively: the empty set, each single option and the full set. A bot
 * that wants the whole space uses `legalAttackers`, `legalBlockersFor` and
 * `targetOptions` directly.
 */
export function legalActions(state: GameState, playerId: string): Action[] {
  const out: Action[] = [];
  if (state.phase === 'over') return out;
  const p = state.players[playerId];
  if (!p) return out;

  if (state.pendingChoices.length > 0) {
    const choice = state.pendingChoices[0];
    if (choice.player !== playerId) return out;
    switch (choice.kind) {
      case 'discard':
        for (const id of p.hand) {
          if (choice.count === 1) out.push({ type: 'Discard', player: playerId, cardInstanceIds: [id] });
        }
        if (choice.count > 1) {
          out.push({ type: 'Discard', player: playerId, cardInstanceIds: p.hand.slice(0, choice.count) });
        }
        break;
      case 'scry':
        out.push({ type: 'Scry', player: playerId, toBottom: [] });
        out.push({ type: 'Scry', player: playerId, toBottom: choice.cards.slice() });
        break;
      case 'sacrifice':
        for (const id of choice.legal) {
          out.push({ type: 'Sacrifice', player: playerId, cardInstanceIds: [id] });
        }
        break;
      case 'chooseTargets': {
        const first = choice.slots.map((s) => s.legal.slice(0, s.count));
        out.push({ type: 'ChooseTargets', player: playerId, stackItemId: choice.stackItemId, targets: first });
        break;
      }
      case 'orderBlockers':
        out.push({
          type: 'OrderBlockers',
          player: playerId,
          attacker: choice.attackerId,
          order: autoOrderBlockers(state, choice.blockerIds),
        });
        break;
      default:
        break;
    }
    return out;
  }

  if (state.phase === 'setup') {
    if (p.redraw === 'pending') {
      out.push({ type: 'KeepHand', player: playerId });
      out.push({ type: 'Redraw', player: playerId });
    }
    return out;
  }

  out.push({ type: 'Concede', player: playerId });

  // Blockers are declared by the defender, who does not hold priority for it.
  if (
    state.phase === 'combat' &&
    state.combat.step === 'declareBlockers' &&
    playerId === opponentOf(state, state.activePlayer)
  ) {
    out.push({ type: 'DeclareBlockers', player: playerId, assignments: [] });
    for (const attackerId of state.combat.attackers) {
      for (const blockerId of legalBlockersFor(state, attackerId, playerId)) {
        const assignments: BlockAssignment[] = [{ blocker: blockerId, attacker: attackerId }];
        try {
          // validateBlocks is read only, so this needs no state clone.
          validateBlocks(state, playerId, assignments);
          out.push({ type: 'DeclareBlockers', player: playerId, assignments });
        } catch {
          // RAID and similar restrictions make some single blocks illegal.
        }
      }
    }
    return out;
  }

  if (state.priorityPlayer !== playerId) return out;

  if (state.phase === 'combat' && state.combat.step === 'declareAttackers' && playerId === state.activePlayer) {
    const legal = legalAttackers(state, playerId);
    out.push({ type: 'DeclareAttackers', player: playerId, attackers: [] });
    for (const id of legal) out.push({ type: 'DeclareAttackers', player: playerId, attackers: [id] });
    if (legal.length > 1) out.push({ type: 'DeclareAttackers', player: playerId, attackers: legal });
    return out;
  }

  const mainPhase = state.phase === 'main1' || state.phase === 'main2';
  const emptyStack = state.stack.length === 0;
  const isActive = playerId === state.activePlayer;

  if (mainPhase && emptyStack && isActive) {
    if (p.walletsInstalledThisTurn < walletInstallAllowance(state, playerId)) {
      for (const id of p.hand) {
        if (profile(state, id).type === 'wallet') {
          out.push({ type: 'InstallWallet', player: playerId, cardInstanceId: id });
        }
      }
    }
  }

  for (const id of p.hand) {
    const prof = profile(state, id);
    if (prof.type === 'wallet') continue;
    const instantSpeed = prof.type === 'exploit';
    if (!instantSpeed && !(mainPhase && emptyStack && isActive)) continue;
    if (!canAfford(state, playerId, id).affordable) continue;
    if (prof.type === 'upgrade' && charactersOf(state, playerId).length === 0) continue;

    const effects = instantSpeed || prof.type === 'transaction' ? spellEffects(prof.abilities) : [];
    const specs = slotSpecs(state, effects, playerId, id);
    const hostOptions = prof.type === 'upgrade' ? charactersOf(state, playerId).map((c) => c.instanceId) : [];

    if (specs.length === 0) {
      if (prof.type === 'upgrade') {
        for (const host of hostOptions) {
          out.push({ type: 'PlayCard', player: playerId, cardInstanceId: id, targets: [[host]] });
        }
      } else {
        out.push({ type: 'PlayCard', player: playerId, cardInstanceId: id, targets: [] });
      }
      continue;
    }
    const unsatisfiable = specs.some((s) => !s.optional && s.legal.length === 0);
    if (unsatisfiable) continue;
    if (specs.length === 1 && specs[0].count === 1) {
      for (const t of specs[0].legal) {
        const targets = prof.type === 'upgrade' ? [[hostOptions[0]], [t]] : [[t]];
        out.push({ type: 'PlayCard', player: playerId, cardInstanceId: id, targets });
      }
      if (specs[0].optional) {
        out.push({ type: 'PlayCard', player: playerId, cardInstanceId: id, targets: [[]] });
      }
    } else {
      const targets = specs.map((s) => s.legal.slice(0, s.count));
      out.push({
        type: 'PlayCard',
        player: playerId,
        cardInstanceId: id,
        targets: prof.type === 'upgrade' ? [[hostOptions[0]], ...targets] : targets,
      });
    }
  }

  // Activated abilities, on the battlefield and in the Dust.
  const sources = [
    ...p.battlefield.characters,
    ...p.battlefield.wallets,
    ...p.battlefield.protocols,
    ...p.battlefield.upgrades,
    ...p.dust,
  ];
  for (const id of sources) {
    const prof = profile(state, id);
    prof.abilities.forEach((ability, abilityIndex) => {
      if (ability.kind !== 'activated') return;
      const act = ability;
      if (act.speed === 'main' && !(mainPhase && emptyStack && isActive)) return;
      const probe: Extract<Action, { type: 'ActivateAbility' }> = {
        type: 'ActivateAbility',
        player: playerId,
        instanceId: id,
        abilityIndex,
      };
      const specs = slotSpecs(state, act.effects, playerId, id);
      if (specs.some((s) => !s.optional && s.legal.length === 0)) return;
      const targets = specs.map((s) => s.legal.slice(0, s.count));
      if (!isActivationPayable(state, playerId, id, act, abilityIndex)) return;
      if (specs.length === 1 && specs[0].count === 1) {
        for (const t of specs[0].legal) {
          out.push({ ...probe, targets: [[t]] });
        }
      } else {
        out.push({ ...probe, targets });
      }
    });
  }

  // Wallets can always be exhausted for Gas through the auto-pay algorithm, so
  // there is no separate tap action.

  if (state.stack.length > 0 || state.pendingAdvance || playerId !== state.activePlayer) {
    out.push({ type: 'Pass', player: playerId });
  }
  if (isActive && emptyStack) {
    if (!(state.phase === 'combat' && state.combat.step === 'declareBlockers')) {
      out.push({ type: 'NextPhase', player: playerId });
    }
  }

  return out;
}

function isActivationPayable(
  state: GameState,
  playerId: string,
  instanceId: string,
  act: ActivatedAbility,
  abilityIndex: number,
): boolean {
  const o = state.objects[instanceId];
  if (!o) return false;
  if (act.cost.removeFromDust ? o.zone !== 'dust' : o.zone !== 'battlefield') return false;
  if (act.oncePerTurn && o.oncePerTurnUsed.includes(`ability:${abilityIndex}`)) return false;
  if (act.cost.exhaust && o.exhausted) return false;
  if (act.cost.payIntegrity && state.players[playerId].integrity <= act.cost.payIntegrity) return false;
  if (act.cost.discard) {
    const hand = state.players[playerId].hand.filter((id) => id !== instanceId);
    if (hand.length < act.cost.discard) return false;
  }
  if (act.cost.sacrifice) {
    const pool = legalTargets(
      state,
      { type: 'permanent', who: 'friendly', mode: 'choose', filter: act.cost.sacrifice },
      playerId,
      instanceId,
    ).filter((id) => id !== instanceId);
    if (pool.length === 0) return false;
  }
  if (act.cost.gas) {
    const plan = planPayment(state, playerId, act.cost.gas, instanceId);
    if (!plan.ok) return false;
  }
  return true;
}

/** Does this player hold a playable Exploit or any-speed ability right now? */
export function hasPlayableResponse(state: GameState, playerId: string): boolean {
  const p = state.players[playerId];
  if (!p || p.lost || p.conceded) return false;
  for (const id of p.hand) {
    const prof = profile(state, id);
    if (prof.type !== 'exploit') continue;
    if (!canAfford(state, playerId, id).affordable) continue;
    const specs = slotSpecs(state, spellEffects(prof.abilities), playerId, id);
    if (specs.some((s) => !s.optional && s.legal.length === 0)) continue;
    return true;
  }
  const sources = [
    ...p.battlefield.characters,
    ...p.battlefield.wallets,
    ...p.battlefield.protocols,
    ...p.battlefield.upgrades,
    ...p.dust,
  ];
  for (const id of sources) {
    const prof = profile(state, id);
    for (let i = 0; i < prof.abilities.length; i++) {
      const ability = prof.abilities[i];
      if (ability.kind !== 'activated' || ability.speed !== 'any') continue;
      if (!isActivationPayable(state, playerId, id, ability, i)) continue;
      const specs = slotSpecs(state, ability.effects, playerId, id);
      if (specs.some((s) => !s.optional && s.legal.length === 0)) continue;
      return true;
    }
  }
  return false;
}

/** Target slot specs for a card in hand, for UI targeting arrows. */
export function targetOptions(state: GameState, playerId: string, instanceId: string) {
  const prof = profile(state, instanceId);
  const effects = prof.type === 'transaction' || prof.type === 'exploit' ? spellEffects(prof.abilities) : [];
  return {
    hosts: prof.type === 'upgrade' ? charactersOf(state, playerId).map((c) => c.instanceId) : [],
    slots: slotSpecs(state, effects, playerId, instanceId),
  };
}

// ---------------------------------------------------------------------------
// Redacted view
// ---------------------------------------------------------------------------

/**
 * What one player is allowed to see. The opponent's hand contents and both
 * deck orders are removed, and hidden objects are stripped from the object map.
 */
export function redactedView(state: GameState, viewer: string): RedactedGameState {
  const clone = cloneState(state);
  const players: RedactedGameState['players'] = {};
  const visible = new Set<string>();

  for (const pid of clone.playerOrder) {
    const p = clone.players[pid];
    const isViewer = pid === viewer;
    players[pid] = {
      ...p,
      deck: [],
      deckCount: p.deck.length,
      hand: isViewer ? p.hand.slice() : [],
      handCount: p.hand.length,
    };
    if (isViewer) for (const id of p.hand) visible.add(id);
    for (const id of [
      ...p.dust,
      ...p.blacklist,
      ...p.battlefield.characters,
      ...p.battlefield.wallets,
      ...p.battlefield.protocols,
      ...p.battlefield.upgrades,
    ]) {
      visible.add(id);
    }
  }
  for (const item of clone.stack) visible.add(item.sourceInstanceId);

  const objects: Record<string, typeof clone.objects[string]> = {};
  for (const id of visible) {
    const o = clone.objects[id];
    if (o) objects[id] = o;
  }

  const rest: Omit<GameState, 'players' | 'objects'> & {
    players?: unknown;
    objects?: unknown;
  } = { ...clone };
  delete rest.players;
  delete rest.objects;
  return { ...(rest as Omit<GameState, 'players' | 'objects'>), players, objects, viewer };
}

// ---------------------------------------------------------------------------
// Replay
// ---------------------------------------------------------------------------

export interface ReplayResult {
  state: GameState;
  events: GameEvent[];
  /** Actions that were rejected, with the error. Empty for a clean replay. */
  errors: { index: number; action: Action; code: string; reason: string }[];
}

/**
 * Re-run a match from its seed, decks and action list. Same inputs always give
 * the same final state, which is what makes bug reports reproducible.
 */
export function replay(
  seed: number | string,
  players: { id: string; name: string; deck: string[] }[],
  actions: Action[],
  options?: Partial<GameOptions>,
): ReplayResult {
  const created = createGame({ seed, players, options });
  let state = created.state;
  const events: GameEvent[] = [...created.events];
  const errors: ReplayResult['errors'] = [];
  actions.forEach((action, index) => {
    try {
      const result = reduce(state, action);
      state = result.state;
      events.push(...result.events);
    } catch (err) {
      if (err instanceof RulesError) {
        errors.push({ index, action, code: err.code, reason: err.reason });
      } else {
        throw err;
      }
    }
  });
  return { state, events, errors };
}

// Re-exported so callers do not need to reach into submodules.
export { collectSlots, legalTargets, slotSpecs };
export { addGas, drawCards, fireTriggers, destroyObject };
export { Rng };
